import React from 'react';
import Backdrop from '@mui/material/Backdrop';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import DialogActions from '@mui/material/DialogActions';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';

const TIME_BUCKETS = [
  [5, null],
  [15, 'less than 15 seconds remaining'],
  [25, '~20 seconds remaining'],
  [35, '~30 seconds remaining'],
  [50, '~45 seconds remaining'],
  [90, '~1 minute remaining'],
  [150, '~2 minutes remaining'],
  [270, '~3-4 minutes remaining'],
  [450, '~5-7 minutes remaining']
];

function getBucketLabel(seconds) {
  for (const [threshold, label] of TIME_BUCKETS) {
    if (seconds <= threshold) {
      return label;
    }
  }

  return 'several minutes remaining';
}

export function calculateTimeLabel(uploadStartTime, progress) {
  if (!uploadStartTime || progress < 10 || progress > 95) {
    return null;
  }

  const elapsed = (Date.now() - uploadStartTime) / 1000;
  const rawEstimate = (elapsed * (100 - progress)) / progress;
  const clampedEstimate = Math.min(600, Math.max(5, rawEstimate));
  return getBucketLabel(clampedEstimate);
}

function NCBIUploadProgress({
  open,
  progress,
  status,
  uploadStartTime,
  completedCount,
  totalCount,
  results,
  onClose
}) {
  const timeLabel = calculateTimeLabel(uploadStartTime, progress);
  const hasErrors = (results?.errors?.length || 0) > 0;
  const isCompleteWithErrors = !open && hasErrors;

  return (
    <Backdrop open={open || isCompleteWithErrors} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
      <Dialog open={open || isCompleteWithErrors} maxWidth="sm" fullWidth>
        <DialogTitle>{isCompleteWithErrors ? 'NCBI Upload Completed with Errors' : 'Uploading from NCBI'}</DialogTitle>
        <DialogContent>
          {isCompleteWithErrors ? (
            <>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                Processed {results?.totalCount || 0} identifiers. {results?.errors?.length || 0} failed.
              </Typography>
              <Box sx={{ maxHeight: 260, overflowY: 'auto', border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
                {results?.errors?.map((errorItem, index) => (
                  <Box key={`${errorItem?.srr || 'unknown'}-${index}`} sx={{ mb: index === (results?.errors?.length || 0) - 1 ? 0 : 1.5 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700 }}>
                      {errorItem?.srr || 'Unknown identifier'}
                    </Typography>
                    <Typography variant="body2" color="textSecondary">
                      {errorItem?.reason || 'Unknown error'}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </>
          ) : (
            <>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
                <img src="/ncbi_logo.png" alt="NCBI" style={{ height: 48 }} />
                <LinearProgress
                  variant="indeterminate"
                  color="secondary"
                  sx={{ width: 96, height: 4, borderRadius: 2 }}
                />
                <img src="/ceph_logo.png" alt="Cepheid" style={{ height: 48 }} />
              </Box>
              <Typography variant="body2" color="textSecondary" sx={{ mb: 2, fontStyle: 'italic' }}>
                Uploading may take a few minutes. Please do not close this window.
              </Typography>
              <Box sx={{ mb: 2 }}>
                <LinearProgress variant="determinate" value={progress} />
                <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                  {completedCount} of {totalCount} processed ({Math.round(progress)}% complete)
                  {timeLabel && (
                    <Box component="span" sx={{ ml: 1, fontSize: '0.75rem', fontWeight: 700 }}>
                      ({timeLabel})
                    </Box>
                  )}
                </Typography>
              </Box>
              <Typography variant="body1">
                {status?.label ? (
                  <>
                    <Box component="span" sx={{ fontWeight: 700 }}>
                      {status.label}:
                    </Box>
                    {' '}{status.detail}
                  </>
                ) : (
                  status?.detail
                )}
              </Typography>
            </>
          )}
        </DialogContent>
        {isCompleteWithErrors && (
          <DialogActions>
            <Button variant="contained" onClick={onClose}>Close</Button>
          </DialogActions>
        )}
      </Dialog>
    </Backdrop>
  );
}

export default NCBIUploadProgress;
